// Full-bleed in-board view editor (position: absolute; inset: 0) scoped to the
// .tree-map container — not a centered modal. Renders the C3 design's top bar
// (Library / title / renders-context · viewType / save-status / Delete / Done)
// and hosts the lettered ConfigCard editor body. Owns create/update/delete
// orchestration. Considered DialogWrapper — rejected: this is a full-page
// surface that owns CRUD, not a centered dialog. See spec Reuse Audit.
//
// visual-coverage-allow: owns live create/update/delete orchestration against
// `api/contextApi` plus `useContextViewPermissions`/`useAspectCatalog`, both
// of which fetch from the backend. Its own spec file mocks all three modules
// at the jest.mock level (module substitution) to render it in isolation —
// a technique Storybook's story environment has no equivalent for here (no
// MSW/API-mock harness in this design-system's .storybook config). Its
// visual surface is exercised through ContextViewEditorOrganism, the editor
// body it hosts, which has its own story.
import React, { useState, useEffect, useRef, useCallback, useMemo } from 'react';
import { useTranslation } from 'localization';
import { Toast } from 'primereact/toast';
import { InputText } from 'primereact/inputtext';
import ButtonAtom from '../../atoms/Button/ButtonAtom';
import ChipAtom from '../../atoms/Chip/ChipAtom';
import DropdownAtom, { DropdownChangeEvent } from '../../atoms/Dropdown/DropdownAtom';
import ConfirmDialogAtom, { confirmDialogAtom } from '../../atoms/ConfirmDialog/ConfirmDialogAtom';
import ContextViewEditorOrganism, {
  ContextViewFormState,
  DEFAULT_VIEW_FORM_STATE,
  PropertyOption,
} from '../ContextViewEditor/ContextViewEditorOrganism';
import { useApiClientsOrNull } from '../../../context/ApiClientsContext';
import { useContextViewPermissions, resetContextViewPermissionsCache } from '../../../hooks/useContextViewPermissions';
import { useAspectCatalog } from '../../../hooks/useAspectCatalog';
import { buildPerspectiveCatalog, collectLevelTypeSystemNames } from '../../../utils/filterPropertyBuilder';
import type { SchemaForFilters } from '../../../utils/filterPropertyBuilder';
import type { ContextLevel } from 'types/context';
import type { BaseViewConfig } from 'types/contextView';
import {
  createContextView,
  updateContextView,
  deleteContextView,
  getContextView,
  getContext,
  setContextViewListing,
} from '../../../api/contextApi';
import {
  viewRecordToViewForm,
  viewFormToCreatePayload,
  viewFormToUpdatePayload,
  generateViewSystemName,
} from '../../../utils/contextViewFormMapping';
import './ContextViewEditorOverlayOrganism.scss';

// ── Types ─────────────────────────────────────────────────────────────────────

export interface ContextViewEditorOverlayOrganismProps {
  mode: 'new' | 'edit';
  contextSystemName: string;
  contextDisplayName: string;
  /** Required in edit mode — the view to load and edit */
  viewSystemName?: string;
  /** Seed form for new mode (the captured current rendering) */
  seedForm?: ContextViewFormState;
  /**
   * The context's root sphere id — the sphere this context (and the Atlas
   * board it's rendering) hangs off. Threaded through to the live preview so
   * `useAvailableTypes` resolves a real sphere instead of firing a doomed
   * request for a fabricated preview id.
   */
  sphereId?: string;
  onSaved: (viewSystemName: string) => void;
  onDeleted: (viewSystemName: string) => void;
  onClose: () => void;
  /**
   * Navigate back to the context library. When omitted, the "Library" button
   * falls back to onClose (close the overlay back to the board).
   * Ignored when `inline` is true — the back button always calls onClose.
   */
  onNavigateToLibrary?: () => void;
  /**
   * Optional analytics event callback. Design-system stays decoupled from the
   * analytics vendor; callers (e.g. TreeMapPage) wire this to useAnalytics().
   */
  onAnalyticsEvent?: (eventName: string, metadata: Record<string, unknown>) => void;
  /** Whether the caller currently has a personalization overlay on the board this editor is attached to (Card A "Reset my adjustments" affordance). */
  hasPersonalizationOverlay?: boolean;
  /** Fired when the user confirms resetting their overlay. Absent while no overlay exists. */
  onResetPersonalization?: () => void;
  /**
   * When true, renders the editor inline (no backdrop, no absolute positioning)
   * so it fills the board area instead of floating over it. Back button label
   * changes to "Board", Walk/Views toggle and Preview are hidden, an
   * "Open context" link and a ✕ close button are added to the topbar.
   * Used by ContextShellOrganism for the TreeMap view editor.
   */
  inline?: boolean;
  /**
   * Called when the user clicks "Open context" in inline mode.
   * Navigates to the context editor for the view's context.
   * Only rendered when `inline` is true and this prop is provided.
   */
  onOpenContext?: () => void;
}

// ── Helpers ───────────────────────────────────────────────────────────────────

function getErrorStatus(error: unknown): number | undefined {
  return (error as { response?: { status?: number } })?.response?.status;
}

// ── Component ─────────────────────────────────────────────────────────────────

const ContextViewEditorOverlayOrganism: React.FC<ContextViewEditorOverlayOrganismProps> = ({
  mode,
  contextSystemName,
  contextDisplayName,
  viewSystemName,
  seedForm,
  sphereId,
  onSaved,
  onDeleted,
  onClose,
  onNavigateToLibrary,
  onAnalyticsEvent,
  hasPersonalizationOverlay,
  onResetPersonalization,
  inline = false,
  onOpenContext,
}) => {
  const { t } = useTranslation();
  const apiClients = useApiClientsOrNull();
  const toastRef = useRef<Toast>(null);

  // Org-wide aspect catalog, unscoped — backs the Kanban card-fields
  // multi-select and the `SphereProperty` aspect picker (EAINT-857 Phase 6).
  // Was never supplied here before EAINT-857 Phase 4, so `propertyOptions`
  // silently defaulted to `[]` on every mount.
  //
  // The catalog option is carried through WHOLE, with only `key` renamed to
  // this editor's `value` (EAINT-899). Naming the fields to keep is what went
  // wrong twice: Phase 4 kept `{label, value}` and lost the kind badge and
  // coverage annotations; the Phase 6 fix added `type`/`ownerTypes` and still
  // lost `group`/`groupLabel`, so the Atlas aspect picker rendered every
  // aspect in the organization as one flat unsectioned list.
  const {
    propertyOptions: aspectCatalog,
    orgFilterSchemas,
    loading: aspectCatalogLoading,
    error: aspectCatalogError,
    refetch: refetchAspectCatalog,
  } = useAspectCatalog(apiClients);
  const propertyOptions = useMemo<PropertyOption[]>(
    () =>
      aspectCatalog.map(({ key, ...option }) => ({ ...option, value: key })),
    [aspectCatalog]
  );

  const initialForm = useMemo<ContextViewFormState>(
    () => seedForm ?? { ...DEFAULT_VIEW_FORM_STATE, contextRef: contextSystemName },
    [seedForm, contextSystemName]
  );

  const [formState, setFormState] = useState<ContextViewFormState>(initialForm);
  const [loading, setLoading] = useState(mode === 'edit');
  const [saving, setSaving] = useState(false);
  const [hasSaved, setHasSaved] = useState(false);
  const [viewMode, setViewMode] = useState<'walk' | 'views'>('walk');
  const [levels, setLevels] = useState<ContextLevel[]>([]);

  // The sphere types this view's context actually walks — scopes both the
  // perspective picker's OUT OF SCOPE badge and the aspect picker's coverage
  // annotation the same way (see `collectLevelTypeSystemNames`). Empty until
  // `levels` loads; both consumers already treat empty as "unknown".
  const aspectScopeTypeSystemNames = useMemo(() => collectLevelTypeSystemNames(levels), [levels]);

  const perspectiveCatalog = useMemo(
    () =>
      buildPerspectiveCatalog({
        schemas: orgFilterSchemas as unknown as SchemaForFilters[],
        scopedTypeSystemNames: aspectScopeTypeSystemNames,
        t,
      }),
    [orgFilterSchemas, aspectScopeTypeSystemNames, t]
  );

  // Listing (visibility) is gated on `publish` on the view — a permission
  // distinct from `edit`, only meaningful once the view exists (edit mode).
  // `permissionsLoading` disables the header's visibility action while the
  // check is in flight rather than hiding it, so the control doesn't pop in
  // once the decision resolves.
  const { canPublish, loading: permissionsLoading } = useContextViewPermissions(
    apiClients,
    mode === 'edit' ? viewSystemName : undefined
  );


  // Snapshot of the last-persisted (or seeded) form — drives the save-status pill.
  const savedSnapshotRef = useRef<string>(JSON.stringify(initialForm));
  const isDirty = JSON.stringify(formState) !== savedSnapshotRef.current;

  // ── Close on Escape ───────────────────────────────────────────────────────

  useEffect(() => {
    const handleKeyDown = (event: KeyboardEvent) => {
      if (event.key === 'Escape') onClose();
    };
    document.addEventListener('keydown', handleKeyDown);
    return () => document.removeEventListener('keydown', handleKeyDown);
  }, [onClose]);

  // ── Edit-mode load ──────────────────────────────────────────────────────────

  useEffect(() => {
    if (mode !== 'edit' || !viewSystemName || !apiClients?.schemaClient) return;

    let cancelled = false;
    setLoading(true);

    getContextView(apiClients.schemaClient, viewSystemName)
      .then((record) => {
        if (!cancelled) {
          const loaded = viewRecordToViewForm(record);
          savedSnapshotRef.current = JSON.stringify(loaded);
          setFormState(loaded);
          setLoading(false);
        }
      })
      .catch(() => {
        if (!cancelled) setLoading(false);
      });

    return () => {
      cancelled = true;
    };
  }, [mode, viewSystemName, apiClients]);

  // ── Load the target context's levels (for the level-overrides rail, card E) ──
  // Always the context this view renders (contextSystemName), never assumed to be
  // whatever context the caller currently has active — new/edit can target a
  // different context than the one on screen.
  useEffect(() => {
    if (!apiClients?.schemaClient || !contextSystemName) return;

    let cancelled = false;

    getContext(apiClients.schemaClient, contextSystemName)
      .then((ctx) => {
        if (!cancelled) setLevels(ctx.definition?.levels ?? []);
      })
      .catch((err) => {
        if (cancelled) return;
        // Card E only renders when levels.length > 0, so a swallowed failure here
        // is indistinguishable from "this context has no levels". Keep the empty
        // fallback but leave a trace so the vanished rail is diagnosable.
        console.error(
          '[ContextViewEditorOverlay] failed to load context levels for level-overrides rail',
          { contextSystemName, err }
        );
        setLevels([]);
      });

    return () => {
      cancelled = true;
    };
  }, [contextSystemName, apiClients]);

  // ── Save ────────────────────────────────────────────────────────────────────

  const handleSave = useCallback(async () => {
    if (!apiClients?.schemaClient) return;
    setSaving(true);

    try {
      if (mode === 'new') {
        const payload = viewFormToCreatePayload(formState);
        let result;
        try {
          result = await createContextView(apiClients.schemaClient, payload);
        } catch (firstError) {
          if (getErrorStatus(firstError) === 409) {
            const retryPayload = {
              ...payload,
              systemName: generateViewSystemName(formState.name, [payload.systemName]),
            };
            result = await createContextView(apiClients.schemaClient, retryPayload);
          } else {
            throw firstError;
          }
        }
        savedSnapshotRef.current = JSON.stringify(formState);
        setHasSaved(true);
        onAnalyticsEvent?.('context_view_created', {
          contextRef: contextSystemName,
          viewType: 'treemap',
          viewSystemName: result.system_name,
          seededFromCurrent: true,
        });
        onSaved(result.system_name);
      } else {
        if (!viewSystemName) return;
        // `updateContextView` REPLACES `base_config`, and this editor owns
        // none of the shared filters (`levelFilters`, `globalFilters`) —
        // `useLevelFilters.commitPendingSharedFilters` does. Read the record
        // back immediately before the PUT and pass the stored values through,
        // so a shared filter committed while this overlay was open is neither
        // reverted (stale form snapshot) nor deleted (key omitted from a
        // wholesale replace). EAINT-789.
        const stored = await getContextView(apiClients.schemaClient, viewSystemName);
        const payload = viewFormToUpdatePayload(
          formState,
          stored.base_config as Partial<BaseViewConfig> | undefined
        );
        await updateContextView(apiClients.schemaClient, viewSystemName, payload);
        savedSnapshotRef.current = JSON.stringify(formState);
        setHasSaved(true);
        onAnalyticsEvent?.('context_view_updated', {
          contextRef: contextSystemName,
          viewSystemName,
        });
        onSaved(viewSystemName);
      }
    } catch (error) {
      const status = getErrorStatus(error);
      if (status === 403) {
        // Same staleness contract as the listing write above: the edit right
        // this save relied on no longer holds, so invalidate the decision.
        resetContextViewPermissionsCache();
        toastRef.current?.show({
          severity: 'error',
          summary: t('context.viewEditorOverlay.error.permissionTitle'),
          detail: t('context.viewEditorOverlay.error.permissionDetail'),
          life: 5000,
        });
      } else {
        toastRef.current?.show({
          severity: 'error',
          summary: t('context.viewEditorOverlay.error.saveTitle'),
          detail: t('context.viewEditorOverlay.error.saveDetail'),
          life: 4000,
        });
      }
    } finally {
      setSaving(false);
    }
  }, [apiClients, mode, formState, viewSystemName, contextSystemName, onSaved, onAnalyticsEvent, t]);

  // ── Delete ──────────────────────────────────────────────────────────────────

  const handleDelete = useCallback(() => {
    if (!apiClients?.schemaClient || !viewSystemName) return;

    confirmDialogAtom({
      message: t('context.viewEditorOverlay.deleteConfirm.message'),
      header: t('context.viewEditorOverlay.deleteConfirm.header'),
      icon: 'pi pi-exclamation-triangle',
      variant: 'danger',
      accept: async () => {
        try {
          await deleteContextView(apiClients.schemaClient, viewSystemName);
          onAnalyticsEvent?.('context_view_deleted', {
            contextRef: contextSystemName,
            viewSystemName,
          });
          onDeleted(viewSystemName);
        } catch {
          toastRef.current?.show({
            severity: 'error',
            summary: t('context.viewEditorOverlay.error.deleteTitle'),
            detail: t('context.viewEditorOverlay.error.deleteDetail'),
            life: 4000,
          });
        }
      },
    });
  }, [apiClients, viewSystemName, contextSystemName, onDeleted, onAnalyticsEvent, t]);

  // ── Listing (visibility) ────────────────────────────────────────────────────

  const handleToggleListing = useCallback(
    (next: boolean) => {
      if (!apiClients?.schemaClient || !viewSystemName) return;

      confirmDialogAtom({
        header: next
          ? t('context.viewEditor.cover.listConfirm.header')
          : t('context.viewEditor.cover.unlistConfirm.header'),
        message: next
          ? t('context.viewEditor.cover.listConfirm.message')
          : t('context.viewEditor.cover.unlistConfirm.message'),
        icon: 'pi pi-info-circle',
        accept: async () => {
          try {
            const result = await setContextViewListing(apiClients.schemaClient, viewSystemName, next);
            // The listing route persists `listed`/`listed_at` and nothing else, so patch
            // exactly that field into the saved snapshot. Re-serialising the whole current
            // form here would adopt any UNSAVED edit as "saved", flipping `isDirty` to false
            // and losing the edit silently when the overlay closes.
            savedSnapshotRef.current = JSON.stringify({
              ...(JSON.parse(savedSnapshotRef.current) as ContextViewFormState),
              listed: result.listed,
            });
            setFormState((prev) => ({ ...prev, listed: result.listed }));
            onAnalyticsEvent?.(next ? 'context_view_listed' : 'context_view_unlisted', {
              viewSystemName,
              contextRef: contextSystemName,
            });
          } catch (error) {
            // A 403 here means the `canPublish` this control acted on is stale.
            // Bump the cache epoch so the next lookup re-fetches instead of
            // leaving the header action enabled against a decision the server
            // has already refused. Contract documented on the hook itself.
            if (getErrorStatus(error) === 403) resetContextViewPermissionsCache();
            toastRef.current?.show({
              severity: 'error',
              summary: next
                ? t('context.viewEditorOverlay.error.listTitle')
                : t('context.viewEditorOverlay.error.unlistTitle'),
              detail: next
                ? t('context.viewEditorOverlay.error.listDetail')
                : t('context.viewEditorOverlay.error.unlistDetail'),
              life: 4000,
            });
          }
        },
      });
    },
    [apiClients, viewSystemName, contextSystemName, onAnalyticsEvent, t]
  );

  // ── Derived chrome state ──────────────────────────────────────────────────

  const isSaveDisabled = saving || loading || !formState.name.default;

  const handleNameChange = useCallback(
    (e: React.ChangeEvent<HTMLInputElement>) => {
      setFormState((prev) => ({ ...prev, name: { ...prev.name, default: e.target.value } }));
    },
    []
  );


  const visibilityOptions = useMemo(
    () => [
      { label: t('context.viewEditor.cover.private'), value: false },
      { label: t('context.viewEditor.cover.listed'), value: true },
    ],
    [t]
  );

  const handleVisibilityChange = useCallback(
    (e: DropdownChangeEvent) => handleToggleListing(Boolean(e.value)),
    [handleToggleListing]
  );

  // saving → "Saving…"; unsaved new draft or edited → "Unsaved"; else → "Saved"
  let saveStatus: 'saving' | 'unsaved' | 'saved' = 'saved';
  if (saving) saveStatus = 'saving';
  else if ((mode === 'new' && !hasSaved) || isDirty) saveStatus = 'unsaved';

  const SAVE_STATUS_LABELS: Record<typeof saveStatus, string> = {
    saving: t('context.viewEditorOverlay.status.saving'),
    unsaved: t('context.viewEditorOverlay.status.unsaved'),
    saved: t('context.viewEditorOverlay.status.allSaved', 'All changes saved'),
  };
  const saveStatusLabel = SAVE_STATUS_LABELS[saveStatus];

  const viewTypeBadge = formState.viewType.toUpperCase();

  // ── Render ──────────────────────────────────────────────────────────────────

  return (
    <div
      className={`context-view-editor-overlay${inline ? ' context-view-editor-overlay--inline' : ''}`}
      role="dialog"
      aria-modal="true"
      aria-label={t('context.viewEditorOverlay.titleEdit', { contextName: contextDisplayName })}
    >
      {!inline && (
        <div
          className="context-view-editor-overlay__backdrop"
          onClick={onClose}
          role="presentation"
          aria-hidden="true"
        />
      )}
      <div className="context-view-editor-overlay__surface">
        {/* Top bar */}
        <div className="context-view-editor-overlay__topbar">
          <ButtonAtom
            className="context-view-editor-overlay__back"
            icon="pi pi-arrow-left"
            label={inline ? t('context.viewEditorOverlay.board', 'Board') : t('context.viewEditorOverlay.library', 'Library')}
            variant="ghost"
            size="small"
            onClick={inline ? onClose : (onNavigateToLibrary ?? onClose)}
          />
          <span className="context-view-editor-overlay__divider" aria-hidden="true" />
          <span className="context-view-editor-overlay__icon" aria-hidden="true">
            <i className="pi pi-th-large" />
          </span>
          <div className="context-view-editor-overlay__identity">
            <InputText
              className="context-view-editor-overlay__title-input"
              value={formState.name.default}
              onChange={handleNameChange}
              placeholder={t('context.viewEditorOverlay.titlePlaceholder')}
              disabled={loading}
              aria-label={t('context.viewEditor.name')}
            />
            <span className="context-view-editor-overlay__subtitle">
              <i className="pi pi-sitemap" aria-hidden="true" />
              <span>{t('context.viewEditorOverlay.renders')}</span>
              <span className="context-view-editor-overlay__context-name">{contextDisplayName}</span>
              <span className="context-view-editor-overlay__dot" aria-hidden="true">·</span>
              <span className="context-view-editor-overlay__type-badge">{viewTypeBadge}</span>
            </span>
          </div>

          <span className="context-view-editor-overlay__divider" aria-hidden="true" />
          <div className="context-view-editor-overlay__visibility">
            <ChipAtom
              className="context-view-editor-overlay__visibility-chip"
              label={
                formState.listed
                  ? t('context.viewEditor.cover.listed')
                  : t('context.viewEditor.cover.private')
              }
              icon={formState.listed ? 'pi pi-globe' : 'pi pi-lock'}
            />
            <DropdownAtom
              className="context-view-editor-overlay__visibility-menu"
              dropdownSize="small"
              value={formState.listed ?? false}
              options={visibilityOptions}
              onChange={handleVisibilityChange}
              disabled={loading || permissionsLoading || !canPublish}
              aria-label={t('context.viewEditorOverlay.visibilityMenuAriaLabel')}
            />
          </div>

          {/* Walk/Views toggle — hidden in inline mode */}
          {!inline && (
            <>
              <span className="context-view-editor-overlay__divider" aria-hidden="true" />
              <div className="context-view-editor-overlay__walk-views-control">
                <button type="button"
                  className={`context-view-editor-overlay__walk-views-btn ${
                    viewMode === 'walk' ? 'context-view-editor-overlay__walk-views-btn--active' : ''
                  }`}
                  onClick={() => setViewMode('walk')}
                  aria-label={t('context.viewEditorOverlay.walk', 'Walk')}
                  aria-pressed={viewMode === 'walk'}
                >
                  <i className="pi pi-sitemap" aria-hidden="true" />
                  <span>{t('context.viewEditorOverlay.walk', 'Walk')}</span>
                </button>
                <button type="button"
                  className={`context-view-editor-overlay__walk-views-btn ${
                    viewMode === 'views' ? 'context-view-editor-overlay__walk-views-btn--active' : ''
                  }`}
                  onClick={() => setViewMode('views')}
                  aria-label={t('context.viewEditorOverlay.views', 'Views')}
                  aria-pressed={viewMode === 'views'}
                >
                  <i className="pi pi-list" aria-hidden="true" />
                  <span>{t('context.viewEditorOverlay.views', 'Views')}</span>
                </button>
              </div>
            </>
          )}

          <span className="context-view-editor-overlay__spacer" />

          {/* Preview button — hidden in inline mode */}
          {!inline && (
            <ButtonAtom
              icon="pi pi-eye"
              label={t('context.viewEditorOverlay.preview', 'Preview')}
              variant="secondary"
              size="small"
              disabled={loading}
            />
          )}

          {/* Status indicator */}
          <span
            className={`context-view-editor-overlay__status context-view-editor-overlay__status--${saveStatus}`}
          >
            {saveStatus === 'saving' ? (
              <i className="pi pi-spin pi-spinner" aria-hidden="true" />
            ) : (
              <span className="context-view-editor-overlay__status-dot" aria-hidden="true" />
            )}
            {saveStatusLabel}
          </span>

          {/* Open context — inline mode only */}
          {inline && onOpenContext && (
            <ButtonAtom
              icon="pi pi-external-link"
              label={t('context.viewEditorOverlay.openContext', 'Open context')}
              variant="secondary"
              size="small"
              onClick={onOpenContext}
              disabled={loading}
            />
          )}

          {mode === 'edit' && (
            <ButtonAtom
              icon="pi pi-trash"
              label={t('context.viewEditorOverlay.delete')}
              variant="danger"
              size="small"
              onClick={handleDelete}
              disabled={saving || loading}
            />
          )}
          <ButtonAtom
            icon="pi pi-check"
            label={t('context.viewEditorOverlay.done')}
            variant="primary"
            size="small"
            onClick={handleSave}
            disabled={isSaveDisabled}
          />
          {inline && (
            <button
              type="button"
              className="context-view-editor-overlay__close-btn"
              onClick={onClose}
              aria-label={t('context.viewEditorOverlay.close', 'Close')}
            >
              <i className="pi pi-times" aria-hidden="true" />
            </button>
          )}
        </div>

        {/* Body */}
        <div className="context-view-editor-overlay__body">
          {loading ? (
            <div className="context-view-editor-overlay__loading">
              <i className="pi pi-spin pi-spinner" aria-label={t('context.viewEditorOverlay.loading')} />
            </div>
          ) : (
            <ContextViewEditorOrganism
              value={formState}
              onChange={setFormState}
              contextDisplayName={contextDisplayName}
              levels={levels}
              lockViewType
              canPublish={canPublish}
              onToggleListing={handleToggleListing}
              hasPersonalizationOverlay={hasPersonalizationOverlay}
              onResetPersonalization={onResetPersonalization}
              onOpenContext={onOpenContext}
              propertyOptions={propertyOptions}
              propertyOptionsLoading={aspectCatalogLoading}
              propertyOptionsError={aspectCatalogError}
              onRetryPropertyOptions={refetchAspectCatalog}
              aspectScopeTypeSystemNames={aspectScopeTypeSystemNames}
              perspectiveCatalog={perspectiveCatalog}
              rootSphereId={sphereId}
            />
          )}
        </div>

        <ConfirmDialogAtom />
        <Toast ref={toastRef} />
      </div>
    </div>
  );
};

export default ContextViewEditorOverlayOrganism;
